import { useLoaderData } from "react-router";

import type { ShellLoader } from "./loader";

/**
 * The payload shape consumers can rely on when `useShellLoaderData()`
 * resolves. Derived from the loader's return type — the `Response` branch
 * used for redirects is excluded because React Router follows redirects
 * internally and never delivers a `Response` to `useLoaderData` at runtime.
 */
export type ShellLoaderData = Exclude<
  Awaited<ReturnType<ShellLoader>>,
  Response
>;

/**
 * Reads the shell loader payload with compile-time typing tied to
 * {@link ShellLoader}. All shell-internal consumers of `useLoaderData`
 * should go through this helper so the loader's return type is the single
 * source of truth — if {@link ShellLoader} changes, every consumer becomes
 * a type error instead of a silent `unknown`-to-cast lie.
 *
 * Also surfaces a clear developer-facing error when used outside the
 * Tinkertoy shell's React Router context (e.g. a consumer renders a shell
 * component outside of the tree created by `mountShell`).
 */
export function useShellLoaderData(): ShellLoaderData {
  try {
    return useLoaderData<ShellLoader>();
  } catch {
    throw new Error(
      "Shell loader data was requested outside the Tinkertoy shell. " +
        "Ensure the component is rendered within the tree created by mountShell().",
    );
  }
}
